import { FC, useEffect, useState, useCallback, memo } from 'react';
import {
  Button,
  TextBody,
  Spacer,
  Stack,
  Icon,
  AccordionGroup,
  AccordionItem,
  BlockList,
  Input,
  Textarea,
  Divider,
  IconButton,
  Tooltip,
} from '@pypestream/design-system';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { NodeColumns, tableStore } from '../../../stores/table-store';
import { useDeleteTaskPopover } from '../../popovers';
import { untrackedIntentSteps } from './untracked-intent-steps';

interface TaskField {
  id: string; // React Hook Form's internal ID
  name: string;
  description?: string;
  taskId?: string; // The actual task ID from the server
}

// Memoized task item component to prevent unnecessary re-renders
/* eslint-disable react/prop-types */
const TaskItem: FC<{
  viewOnly?: boolean;
  field: { id: string };
  index: number;
  isExpanded: boolean;
  onShow: () => void;
  onHide: () => void;
  onRemove: () => void;
}> = memo(({ viewOnly, index, isExpanded, onShow, onHide, onRemove }) => {
  const { control, register, setValue, getValues } = useFormContext();

  // Only watch the specific task's name for the header display
  const taskName = useWatch({
    control,
    name: `tasks.${index}.name`,
    defaultValue: '',
  });

  // Get taskId from form values
  const taskId = useWatch({
    control,
    name: `tasks.${index}.taskId`,
    defaultValue: undefined,
  });

  const { deleteTaskPopover, trigger } = useDeleteTaskPopover({
    testId: `ps-agents-delete-task-popover-${index}`,
    taskName: taskName || undefined,
    onDelete: onRemove,
  });

  return (
    <AccordionItem
      test-id={`ps-agents-task-item-${index}`}
      open={isExpanded}
      onShow={onShow}
      onHide={onHide}
    >
      <Stack
        slot="header"
        justifyContent="space-between"
        alignItems="center"
        gutter="xsmall"
        nowrap
        style={{ flexGrow: 1 }}
      >
        <Stack alignItems="center" gutter="xsmall" nowrap>
          <Icon size="small" name="target" color="purple" />
          <TextBody size="small">{taskName || ''}</TextBody>
        </Stack>
        {!viewOnly && (
          <>
            <IconButton
              id={trigger}
              test-id={`ps-agents-task-delete-btn-${index}`}
              name="delete"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
            {deleteTaskPopover}
          </>
        )}
      </Stack>
      <BlockList gutter="xsmall">
        <div>
          <TextBody size="small">Task Name</TextBody>
          <Spacer size="2xsmall" />
          <Input
            test-id={`ps-agents-task-name-input-${index}`}
            variant="outlined"
            placeholder="Order shoes"
            {...register(`tasks.${index}.name`)}
            onInput={(e) => {
              // @ts-expect-error - TODO: fix this
              const { value } = e.target;
              setValue(`tasks.${index}.name`, value, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
            onChange={({ target: { value } }) => {
              if (taskId) {
                const currentTask = getValues(`tasks.${index}`) as TaskField;
                tableStore.updateTaskNode(taskId, {
                  name: value,
                  description: currentTask?.description || '',
                });
              }
            }}
            disabled={viewOnly}
          />
        </div>
        <div>
          <Stack alignItems="center" gutter="2xsmall">
            <TextBody size="small">Example Phrases*</TextBody>
            <Tooltip
              placement="right"
              content="Write at least one natural way someone might ask for this. The Supervisor Agent uses these Example Phrases to understand what the user wants and route them to the right workflow."
            >
              <Icon name="info" color="gray" size="xsmall" />
            </Tooltip>
          </Stack>
          <Spacer size="2xsmall" />
          <Textarea
            test-id={`ps-agents-task-description-textarea-${index}`}
            variant="outlined"
            placeholder="I want to buy shoes | Help me order sneakers"
            {...register(`tasks.${index}.description`)}
            onChange={({ target: { value } }) => {
              if (taskId) {
                const currentTask = getValues(`tasks.${index}`) as TaskField;
                tableStore.updateTaskNode(taskId, {
                  name: currentTask?.name || '',
                  description: value,
                });
              }
            }}
            disabled={viewOnly}
          />
        </div>
      </BlockList>
    </AccordionItem>
  );
});

TaskItem.displayName = 'TaskItem';

/**
 * Steps the Supervisor also routes to that have no saved task, e.g. the one
 * an agent template adds. They read and edit the step itself: the phrases
 * are its Example Phrases, written to it when the field is left. There is no
 * delete here — removing one means removing the step, which belongs in Build.
 */
const UntrackedIntentSteps: FC<{ viewOnly?: boolean }> = observer(
  ({ viewOnly }) => {
    const { control } = useFormContext();
    const tasks = useWatch({ control, name: 'tasks' }) as
      | TaskField[]
      | undefined;
    const taskIds = new Set(
      (tasks ?? []).map((t) => t.taskId).filter((id): id is string => !!id)
    );
    const steps = untrackedIntentSteps(tableStore.getAllSortedRows(), taskIds);
    const [open, setOpen] = useState<Set<string>>(new Set());

    return (
      <>
        {steps.map((step) => (
          <AccordionItem
            key={step.id}
            test-id={`ps-agents-intent-step-${step.nodeNumber}`}
            open={open.has(step.id)}
            onShow={() => setOpen((prev) => new Set(prev).add(step.id))}
            onHide={() =>
              setOpen((prev) => {
                const next = new Set(prev);
                next.delete(step.id);
                return next;
              })
            }
          >
            <Stack
              slot="header"
              alignItems="center"
              gutter="xsmall"
              nowrap
              style={{ flexGrow: 1 }}
            >
              <Icon size="small" name="target" color="purple" />
              <TextBody size="small">{step.nodeName || ''}</TextBody>
              <TextBody size="small" variant="secondary">
                {step.nodeNumber}
              </TextBody>
            </Stack>
            <BlockList gutter="xsmall">
              <div>
                <TextBody size="small">Example Phrases</TextBody>
                <Spacer size="2xsmall" />
                <Textarea
                  test-id={`ps-agents-intent-step-phrases-${step.nodeNumber}`}
                  variant="outlined"
                  value={step.utterances ?? ''}
                  onChange={({ target: { value } }) => {
                    if (value !== (step.utterances ?? '')) {
                      tableStore.writeCellValue(
                        step.id,
                        NodeColumns.utterances,
                        value
                      );
                    }
                  }}
                  disabled={viewOnly}
                />
              </div>
            </BlockList>
          </AccordionItem>
        ))}
      </>
    );
  }
);

export const TabTasks: FC<{ viewOnly?: boolean }> = ({ viewOnly }) => {
  const { control, getValues } = useFormContext();

  const {
    fields,
    append,
    remove: removeTask,
  } = useFieldArray({
    control,
    name: 'tasks',
  });

  const [expandedTaskIds, setExpandedTaskIds] = useState<Set<number>>(
    new Set()
  );

  // Sync tasks with nodes when component mounts or tasks change
  // Use getValues instead of useWatch to avoid re-renders
  useEffect(() => {
    const tasks = getValues('tasks') as TaskField[];
    if (!tasks) return;

    const tasksWithIds = tasks
      .filter((task: TaskField) => task.taskId)
      .map((task: TaskField) => ({
        id: task.taskId!,
        name: task.name,
        description: task.description || '',
      }));

    if (tasksWithIds.length > 0) {
      tableStore.syncTasksWithNodes(tasksWithIds);
    }
  }, [fields, getValues]);

  const addNewTask = useCallback(() => {
    const newTask = {
      name: '',
      description: '',
      // No taskId for new tasks - they will be created on the server
    };
    append(newTask);
    setExpandedTaskIds((prev) => new Set(prev).add(fields.length));
  }, [append, fields.length]);

  // Memoize callbacks to prevent unnecessary re-renders of TaskItem
  const handleShow = useCallback((index: number) => {
    setExpandedTaskIds((prev) => new Set(prev).add(index));
  }, []);

  const handleHide = useCallback((index: number) => {
    setExpandedTaskIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
  }, []);

  const handleRemove = useCallback(
    (index: number) => {
      removeTask(index);
    },
    [removeTask]
  );

  return (
    <>
      <Stack nowrap gutter="small"></Stack>
      <Spacer />
      <AccordionGroup multiple>
        {fields.map((field, index) => (
          <TaskItem
            viewOnly={viewOnly}
            key={field.id}
            field={field}
            index={index}
            isExpanded={expandedTaskIds.has(index)}
            onShow={() => handleShow(index)}
            onHide={() => handleHide(index)}
            onRemove={() => handleRemove(index)}
          />
        ))}
        <UntrackedIntentSteps viewOnly={viewOnly} />
      </AccordionGroup>
      {!viewOnly && (
        <>
          <Divider />
          <Spacer size="xsmall" />
          <Button
            test-id="ps-agents-add-task-btn"
            size="large"
            variant="ghost"
            onClick={addNewTask}
          >
            <Icon
              name="plus"
              size="small"
              color="purple"
              variant="filled"
              slot="prefix"
            />
            Add task
          </Button>
        </>
      )}
    </>
  );
};
